import { useState, useRef, useEffect } from 'react';

interface ServiceFilterProps {
  services: any[];
  selectedIds: string[];
  onSelectionChange: (ids: string[]) => void;
  loading: boolean;
}

export default function ServiceFilter({
  services,
  selectedIds,
  onSelectionChange,
  loading,
}: ServiceFilterProps) {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  const handleToggleService = (serviceId: string) => {
    if (selectedIds.includes(serviceId)) {
      onSelectionChange(selectedIds.filter((id) => id !== serviceId));
    } else {
      onSelectionChange([...selectedIds, serviceId]);
    }
  };

  const handleSelectAll = () => {
    onSelectionChange(services.map((service) => service.id));
  };

  const handleClearAll = () => {
    onSelectionChange([]);
  };

  const allSelected = services.length > 0 && selectedIds.length === services.length;

  const buttonText = () => {
    if (loading) return 'Loading...';
    if (selectedIds.length === 0) return 'All Services';
    if (allSelected) return 'All Services';
    return `${selectedIds.length} Service${selectedIds.length === 1 ? '' : 's'}`;
  };

  const formatDuration = (minutes?: number) => {
    if (!minutes) return null;
    if (minutes < 60) return `${minutes} min`;
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    if (mins === 0) return `${hours} hr`;
    return `${hours}h ${mins}m`;
  };

  return (
    <div className="relative" ref={dropdownRef}>
      {/* Dropdown Button */}
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        disabled={loading}
        className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-300 rounded-lg shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
      >
        {loading ? (
          <svg
            className="animate-spin h-4 w-4 text-gray-600"
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
          >
            <circle
              className="opacity-25"
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              strokeWidth="4"
            />
            <path
              className="opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
            />
          </svg>
        ) : (
          <svg
            className="h-4 w-4 text-gray-600"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z"
            />
          </svg>
        )}
        <span className="text-sm font-medium text-gray-700">{buttonText()}</span>
        <svg
          className={`h-4 w-4 text-gray-600 transition-transform ${isOpen ? 'rotate-180' : ''}`}
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {/* Dropdown Menu */}
      {isOpen && !loading && (
        <div className="absolute z-50 mt-2 w-72 bg-white border border-gray-200 rounded-lg shadow-lg">
          {/* Select All / Clear */}
          <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 bg-gray-50">
            <button
              type="button"
              onClick={handleSelectAll}
              disabled={allSelected}
              className="text-sm font-medium text-blue-600 hover:text-blue-700 disabled:text-gray-400 disabled:cursor-not-allowed"
            >
              Select All
            </button>
            <button
              type="button"
              onClick={handleClearAll}
              disabled={selectedIds.length === 0}
              className="text-sm font-medium text-gray-600 hover:text-gray-700 disabled:text-gray-400 disabled:cursor-not-allowed"
            >
              Clear
            </button>
          </div>

          {/* Service List */}
          <div className="max-h-64 overflow-y-auto">
            {services.length === 0 ? (
              <div className="px-4 py-3 text-sm text-gray-500">No services available</div>
            ) : (
              services.map((service) => {
                const isChecked = selectedIds.includes(service.id);
                const duration = formatDuration(service.durationMinutes);

                return (
                  <label
                    key={service.id}
                    className="flex items-center px-4 py-3 hover:bg-gray-50 cursor-pointer border-b border-gray-100 last:border-b-0"
                  >
                    <input
                      type="checkbox"
                      checked={isChecked}
                      onChange={() => handleToggleService(service.id)}
                      className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500 focus:ring-2"
                    />
                    <div className="ml-3 flex-1">
                      <div className="text-sm font-medium text-gray-900">{service.name}</div>
                      {duration && (
                        <div className="text-xs text-gray-500 mt-0.5">{duration}</div>
                      )}
                    </div>
                  </label>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
}
